import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from '../../components/Footer';
import { Helmet } from 'react-helmet';

export function Tools() {
  const tools = [
    {
      title: 'SAT Score Calculator',
      description: 'Calculate your Digital SAT score with our adaptive scoring system aligned to the latest format.',
      path: '/sat-score-calculator',
      icon: '📊',
      category: 'college-entrance'
    },
    {
      title: 'ACT Score Calculator',
      description: 'Convert your raw ACT scores to scaled scores and composite score using current scoring tables.',
      path: '/act-score-calculator',
      icon: '📈',
      category: 'college-entrance'
    },
    {
      title: 'AP US History Calculator',
      description: 'Calculate your AP US History score based on multiple choice and free response questions.',
      path: '/apush-score-calculator',
      icon: '🗽',
      category: 'ap-history'
    },
    {
      title: 'AP Language Calculator',
      description: 'Calculate your AP English Language score with College Board scoring guidelines.',
      path: '/ap-lang-score-calculator',
      icon: '📝',
      category: 'ap-english'
    },
    {
      title: 'AP Biology Calculator',
      description: 'Calculate your AP Biology exam score with our free scoring tool using the latest guidelines.',
      path: '/ap-bio-score-calculator',
      icon: '🧬',
      category: 'ap-science'
    },
    {
      title: 'AP Chemistry Calculator',
      description: 'Calculate your AP Chemistry exam score with official College Board scoring criteria.',
      path: '/ap-chem-score-calculator',
      icon: '⚗️',
      category: 'ap-science'
    },
    {
      title: 'AP Literature Calculator',
      description: 'Calculate your AP English Literature score with our free scoring calculator.',
      path: '/ap-lit-score-calculator',
      icon: '📚',
      category: 'ap-english'
    },
    {
      title: 'AP Government Calculator',
      description: 'Calculate your AP Government exam score with our accurate scoring tool.',
      path: '/ap-gov-score-calculator',
      icon: '⚖️',
      category: 'ap-history'
    },
    {
      title: 'AP Psychology Calculator',
      description: 'Calculate your AP Psychology exam score with our free scoring calculator using the latest guidelines.',
      path: '/ap-psych-score-calculator',
      icon: '🧠',
      category: 'ap-social'
    },
    {
      title: 'AP Calculus AB Calculator',
      description: 'Calculate your AP Calculus AB score with our free scoring tool using the latest guidelines.',
      path: '/ap-calc-ab-score-calculator',
      icon: '📐',
      category: 'ap-math'
    },
    {
      title: 'AP Calculus BC Calculator',
      description: 'Calculate your AP Calculus BC score with our free scoring calculator.',
      path: '/ap-calc-bc-score-calculator',
      icon: '📏',
      category: 'ap-math'
    },
    {
      title: 'AP World History Calculator',
      description: 'Calculate your AP World History exam score with our accurate scoring tool.',
      path: '/ap-world-score-calculator',
      icon: '🌍',
      category: 'ap-history'
    },
    {
      title: 'AP Physics Calculator',
      description: 'Calculate your AP Physics exam score with our free scoring calculator.',
      path: '/ap-physics-score-calculator',
      icon: '⚡',
      category: 'ap-science'
    },
    {
      title: 'AP Computer Science A Calculator',
      description: 'Calculate your AP CSA exam score with our free scoring tool using the latest guidelines.',
      path: '/ap-csa-score-calculator',
      icon: '💻',
      category: 'ap-stem'
    },
    {
      title: 'AP European History Calculator',
      description: 'Calculate your AP European History exam score with our accurate scoring calculator.',
      path: '/ap-euro-score-calculator',
      icon: '🏰',
      category: 'ap-history'
    },
    {
      title: 'AP Macroeconomics Calculator',
      description: 'Calculate your AP Macroeconomics exam score with our free scoring tool.',
      path: '/ap-macro-score-calculator',
      icon: '📊',
      category: 'ap-social'
    },
    {
      title: 'AP Physics 1 Calculator',
      description: 'Calculate your AP Physics 1 exam score with our free scoring calculator using the latest guidelines.',
      path: '/ap-physics-1-score-calculator',
      icon: '⚛️',
      category: 'ap-science'
    },
    {
      title: 'AP Physics C Calculator',
      description: 'Calculate your AP Physics C exam score with our accurate scoring tool.',
      path: '/ap-physics-c-score-calculator',
      icon: '🔬',
      category: 'ap-science'
    },
    {
      title: 'AP Computer Science Principles Calculator',
      description: 'Calculate your AP CSP exam score with our free scoring calculator using the latest guidelines.',
      path: '/ap-csp-score-calculator',
      icon: '🖥️',
      category: 'ap-stem'
    }
  ];

  // Group tools by category for better organization
  const categories = {
    'college-entrance': {
      title: 'College Entrance Exams',
      description: 'Calculate scores for SAT and ACT exams'
    },
    'ap-science': {
      title: 'AP Science Exams',
      description: 'Score calculators for AP Biology, Chemistry, Physics and more'
    },
    'ap-math': {
      title: 'AP Mathematics Exams',
      description: 'Score calculators for AP Calculus AB, BC and Statistics'
    },
    'ap-english': {
      title: 'AP English Exams',
      description: 'Score calculators for AP Language and Literature'
    },
    'ap-history': {
      title: 'AP History Exams',
      description: 'Score calculators for AP US History, World History, and European History'
    },
    'ap-social': {
      title: 'AP Social Sciences',
      description: 'Score calculators for AP Psychology, Economics, and Government'
    },
    'ap-stem': {
      title: 'AP Computer Science',
      description: 'Score calculators for AP Computer Science A and Principles'
    }
  };

  const groupedTools = {};
  tools.forEach(tool => {
    if (!groupedTools[tool.category]) {
      groupedTools[tool.category] = [];
    }
    groupedTools[tool.category].push(tool);
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Add Helmet for enhanced SEO */}
      <Helmet>
        <title>Educational Score Calculators | Free Test Score Estimators</title>
        <meta name="description" content="Free score calculators for SAT, ACT, and AP exams. Calculate your scores instantly with accurate tools aligned to current exam formats." />
        <meta name="keywords" content="score calculator, SAT calculator, ACT calculator, AP score calculator, test score estimator, college entrance exam calculator, AP exam score" />
        <link rel="canonical" href="https://mocktestpro.com/tools" />
        
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content="Educational Score Calculators | Free Test Score Estimators" />
        <meta property="og:description" content="Calculate your SAT, ACT, and AP exam scores with our free tools aligned to current exam formats." />
        <meta property="og:url" content="https://mocktestpro.com/tools" />
        <meta property="og:type" content="website" />
        
        {/* Structured data for rich results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": tools.map((tool, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "SoftwareApplication",
                "name": tool.title,
                "description": tool.description,
                "applicationCategory": "EducationalApplication",
                "operatingSystem": "Web",
                "offers": {
                  "@type": "Offer",
                  "price": "0",
                  "priceCurrency": "USD"
                }
              }
            }))
          })}
        </script>
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl font-bold text-gray-800 mb-3">
          Educational Score Calculators
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Free tools to calculate and estimate your scores for SAT, ACT, and AP exams. 
          Aligned to current exam formats with official scoring guidelines.
        </p>
      </motion.div>

      <section className="mb-12 bg-blue-50 p-6 rounded-lg">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Why Use Our Score Calculators?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white p-5 rounded-lg shadow">
            <div className="text-3xl mb-2">✓</div>
            <h3 className="text-lg font-semibold mb-2">Accurate Scoring</h3>
            <p className="text-gray-600">Based on official scoring guidelines from College Board and ACT, Inc.</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <div className="text-3xl mb-2">✓</div>
            <h3 className="text-lg font-semibold mb-2">Regularly Updated</h3>
            <p className="text-gray-600">All calculators reflect the latest exam formats and scoring methods</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <div className="text-3xl mb-2">✓</div>
            <h3 className="text-lg font-semibold mb-2">Completely Free</h3>
            <p className="text-gray-600">No registration or payment required to use any of our tools</p>
          </div>
        </div>
      </section>

      {/* Display tools by category */}
      {Object.keys(categories).map((categoryKey) => (
        <section key={categoryKey} className="mb-12">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2"
          >
            {categories[categoryKey].title}
          </motion.h2>
          <p className="text-gray-600 mb-6">{categories[categoryKey].description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupedTools[categoryKey]?.map((tool, index) => (
              <motion.div
                key={tool.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/tools${tool.path}`}
                  className="block h-full bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-6"
                  aria-label={`Use ${tool.title}`}
                >
                  <div className="text-4xl mb-4">{tool.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {tool.title}
                  </h3>
                  <p className="text-gray-600">
                    {tool.description}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      ))}

      <section className="my-12 bg-gray-50 p-6 rounded-lg">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use Our Score Calculators</h2>
        <ol className="list-decimal pl-6 space-y-3">
          <li className="text-gray-700">Select the calculator for your specific exam</li>
          <li className="text-gray-700">Enter your raw scores or the number of questions you answered correctly</li>
          <li className="text-gray-700">For AP exams, also enter your free response scores</li>
          <li className="text-gray-700">Get an instant estimate of your final score</li>
          <li className="text-gray-700">Use the information to track your progress and set study goals</li>
        </ol>
      </section>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-12 text-center text-gray-600 bg-white p-6 rounded-lg shadow-sm"
      >
        <h2 className="text-xl font-semibold mb-3">About Our Calculators</h2>
        <p>All calculators are updated for current exam formats and scoring guidelines.</p>
        <p className="mt-2">Our tools provide estimates based on official scoring methods, but actual scores may vary.</p>
        <p className="mt-4 text-sm">
          <strong>Note:</strong> SAT®, ACT®, AP®, and Advanced Placement® are registered trademarks of their respective owners 
          (College Board, ACT, Inc.), which are not affiliated with and do not endorse these calculators.
        </p>
      </motion.div>

      <section className="my-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div className="bg-white p-5 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">How accurate are these score calculators?</h3>
            <p className="text-gray-600">Our calculators use official scoring guidelines from College Board and ACT, Inc. to provide the most accurate estimates possible. However, actual test scoring may vary slightly due to equating processes and specific test administrations.</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">Are these calculators free to use?</h3>
            <p className="text-gray-600">Yes, all our score calculators are completely free to use with no registration required. We believe in providing accessible tools to help students prepare for their exams.</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">How often are the calculators updated?</h3>
            <p className="text-gray-600">We update our calculators regularly to reflect the latest exam formats and scoring guidelines.</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">Do these calculators store my scores?</h3>
            <p className="text-gray-600">No, these calculators do not store or save your scores. All calculations are performed locally in your browser, and no data is sent to our servers.</p>
          </div>
        </div>
      </section>

      <section className="my-12 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Popular Score Calculators</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Link 
            to="/tools/sat-score-calculator" 
            className="bg-white p-5 rounded-lg shadow hover:shadow-md transition-shadow flex items-center"
          >
            <div className="text-4xl mr-4">📊</div>
            <div>
              <h3 className="text-lg font-semibold">SAT Score Calculator</h3>
              <p className="text-gray-600">Updated for the latest Digital SAT format</p>
            </div>
          </Link>
          <Link 
            to="/tools/act-score-calculator" 
            className="bg-white p-5 rounded-lg shadow hover:shadow-md transition-shadow flex items-center"
          >
            <div className="text-4xl mr-4">📈</div>
            <div>
              <h3 className="text-lg font-semibold">ACT Score Calculator</h3>
              <p className="text-gray-600">Calculate your composite ACT score</p>
            </div>
          </Link>
          <Link 
            to="/tools/ap-calc-ab-score-calculator" 
            className="bg-white p-5 rounded-lg shadow hover:shadow-md transition-shadow flex items-center"
          >
            <div className="text-4xl mr-4">📐</div>
            <div>
              <h3 className="text-lg font-semibold">AP Calculus AB Calculator</h3>
              <p className="text-gray-600">Estimate your AP Calc AB score</p>
            </div>
          </Link>
          <Link 
            to="/tools/apush-score-calculator" 
            className="bg-white p-5 rounded-lg shadow hover:shadow-md transition-shadow flex items-center"
          >
            <div className="text-4xl mr-4">🗽</div>
            <div>
              <h3 className="text-lg font-semibold">AP US History Calculator</h3>
              <p className="text-gray-600">Calculate your APUSH exam score</p>
            </div>
          </Link>
        </div>
      </section>

      <section className="my-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Exam Preparation Resources</h2>
        <p className="mb-6">In addition to our score calculators, we offer a variety of resources to help you prepare for your exams:</p>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white p-5 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">Practice Tests</h3>
            <p className="text-gray-600 mb-3">Take full-length practice tests for SAT, ACT, and AP exams to gauge your readiness.</p>
            <Link to="/practice-tests" className="text-blue-600 hover:underline">View Practice Tests →</Link>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">Study Guides</h3>
            <p className="text-gray-600 mb-3">Access comprehensive study guides for all major standardized tests.</p>
            <Link to="/study-guides" className="text-blue-600 hover:underline">View Study Guides →</Link>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">Exam Tips</h3>
            <p className="text-gray-600 mb-3">Learn test-taking strategies and tips to maximize your scores.</p>
            <Link to="/exam-tips" className="text-blue-600 hover:underline">View Exam Tips →</Link>
          </div>
        </div>
      </section>

      <section className="my-12 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Additional Calculator Resources</h2>
        <p className="mb-4">Looking for other educational calculators like attendance trackers, student loan calculators, or GPA calculators?</p>
        <div className="flex justify-center">
          <a 
            href="/tools/external-calculators" 
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            View External Calculator Resources
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

